/**
 * Charging Session Info Section Component
 * Matches Flutter's blade_screen.dart Section 3: Charging Session Info
 *
 * Fields:
 * - Row 1: Machine ID | EVSE ID | Connector ID
 * - Row 2: Connector Type | UpdatedDT
 * - Extra: Machine Name, Actor Hierarchy (from API)
 */

import { SessionDetail } from '../../types/session-types';
import { formatDateTime } from '../../utils/date-utils';

interface ChargingInfoSectionProps {
  detail: SessionDetail;
}

export function ChargingInfoSection({ detail }: ChargingInfoSectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">
        Charging Session Info
      </h3>

      {/* Row 1: Machine ID | EVSE ID | Connector ID */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <InfoItem
          label="Machine ID"
          value={detail.machineId?.toString() ?? '--'}
        />
        <InfoItem
          label="EVSE ID"
          value={detail.connectorId?.toString() ?? '--'}
        />
        <InfoItem
          label="Connector ID"
          value={detail.connectorId?.toString() ?? '--'}
        />
      </div>

      {/* Row 2: Connector Type | Updated DT */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <InfoItem label="Connector Type" value={detail.connectorType ?? '--'} />
        <InfoItem
          label="Updated DT"
          value={formatDateTime(detail.updated)}
          className="col-span-2"
        />
      </div>

      {/* HIVE-6307: Renamed Machine Name to Charging Station Name, added Charging Station Number */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {detail.locationName && (
          <InfoItem label="Charging Station Name" value={detail.locationName} />
        )}
        {detail.chargePointNumber && (
          <InfoItem label="Charging Station Number" value={detail.chargePointNumber} />
        )}
        {detail.physicalReference && (
          <InfoItem label="Physical Ref" value={detail.physicalReference} />
        )}
      </div>

      {/* Extra from API: Actor Hierarchy */}
      <div className="grid grid-cols-3 gap-4">
        {detail.kioskActorHierarchy && (
          <InfoItem
            label="Actor Hierarchy"
            value={detail.kioskActorHierarchy}
            className="col-span-2"
          />
        )}
      </div>
    </div>
  );
}

interface InfoItemProps {
  label: string;
  value: string;
  className?: string;
}

function InfoItem({ label, value, className = '' }: InfoItemProps) {
  return (
    <div className={className}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900 truncate" title={value}>
        {value}
      </div>
    </div>
  );
}
